import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const isOfficeDocFileType = fileType =>
  fileType.includes('word') || fileType.includes('excel') || fileType.startsWith('text/');

const isPreviewableFileType = fileType =>
  fileType === 'application/pdf' ||
  fileType.startsWith('audio/') ||
  fileType.startsWith('video/') ||
  isOfficeDocFileType(fileType);

const fileTypeIcon = fileType => {
  if (fileType === 'application/pdf') return 'file-pdf';
  if (fileType.startsWith('audio/')) return 'music';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.includes('word')) return 'file-word';
  if (fileType.includes('excel')) return 'file-excel';
  if (fileType.startsWith('text/')) return 'file-alt';
  return 'file';
};

function FileLinkComponent({ fileUrl, fileName, fileType }) {
  const [showPreview, setShowPreview] = useState(false);
  const canPreview = isPreviewableFileType(fileType);

  return (
    <span className="editor-file-link">
      <span className="editor-file-link-name">
        <FontAwesomeIcon icon={fileTypeIcon(fileType)} className="editor-file-link-icon" />
        {fileName}
      </span>
      <a
        href={fileUrl}
        download={fileName}
        target="_blank"
        rel="noreferrer"
        className="editor-file-download-toggle"
        title="Download"
      >
        <FontAwesomeIcon icon="download" />
      </a>
      {canPreview && (
        <button
          type="button"
          className="editor-file-preview-toggle"
          title={showPreview ? 'Hide preview' : 'Preview'}
          onClick={() => setShowPreview(previous => !previous)}
        >
          <FontAwesomeIcon icon="eye" />
        </button>
      )}
      {showPreview && (
        <span className="editor-file-preview">
          {fileType === 'application/pdf' && (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
              width="100%"
              height="600px"
              title={fileName}
            />
          )}
          {fileType.startsWith('audio/') && <audio controls src={fileUrl} />}
          {fileType.startsWith('video/') && <video controls width="100%" src={fileUrl} />}
          {isOfficeDocFileType(fileType) && (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
              width="100%"
              height="400px"
              title={fileName}
            />
          )}
        </span>
      )}
    </span>
  );
}

FileLinkComponent.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  fileType: PropTypes.string
};

export class FileLinkNode extends DecoratorNode {
  static getType() {
    return 'file-link';
  }

  static clone(node) {
    return new FileLinkNode(node.__fileUrl, node.__fileName, node.__fileType, node.__key);
  }

  static importJSON(serializedNode) {
    const { fileUrl, fileName, fileType } = serializedNode;
    return $createFileLinkNode({ fileUrl, fileName, fileType });
  }

  exportJSON() {
    return {
      type: 'file-link',
      version: 1,
      fileUrl: this.__fileUrl,
      fileName: this.__fileName,
      fileType: this.__fileType
    };
  }

  static importDOM() {
    return {
      span: domNode => {
        if (!domNode.hasAttribute('data-file-url')) return null;
        return {
          conversion: element => ({
            node: $createFileLinkNode({
              fileUrl: element.getAttribute('data-file-url') || '',
              fileName: element.getAttribute('data-file-name') || '',
              fileType: element.getAttribute('data-file-type') || ''
            })
          }),
          priority: 1
        };
      }
    };
  }

  exportDOM() {
    const element = document.createElement('span');
    element.setAttribute('class', 'editor-file-link');
    element.setAttribute('data-file-url', this.__fileUrl);
    element.setAttribute('data-file-name', this.__fileName);
    element.setAttribute('data-file-type', this.__fileType);

    const link = document.createElement('a');
    link.setAttribute('href', this.__fileUrl);
    link.setAttribute('download', this.__fileName);
    link.textContent = this.__fileName;
    element.appendChild(link);

    return { element };
  }

  constructor(fileUrl, fileName, fileType, key) {
    super(key);
    this.__fileUrl = fileUrl;
    this.__fileName = fileName || '';
    this.__fileType = fileType || '';
  }

  createDOM() {
    return document.createElement('span');
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <FileLinkComponent fileUrl={this.__fileUrl} fileName={this.__fileName} fileType={this.__fileType} />;
  }
}

export function $createFileLinkNode({ fileUrl, fileName = '', fileType = '' }) {
  return $applyNodeReplacement(new FileLinkNode(fileUrl, fileName, fileType));
}

export function $isFileLinkNode(node) {
  return node instanceof FileLinkNode;
}
